import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FAQ.css';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      category: 'General',
      questions: [
        {
          q: 'What is SafeHer?',
          a: 'SafeHer is a community information-sharing platform where women can share and view real-time safety reports. Our mission is to create early awareness of patterns and situations in your area. SafeHer is not an emergency service — for immediate danger, always contact your local emergency services.'
        },
        {
          q: 'Is SafeHer free to use?',
          a: 'Yes! SafeHer is completely free for all users. We believe safety information should be accessible to everyone.'
        },
        {
          q: 'What devices does SafeHer work on?',
          a: 'SafeHer works on all modern web browsers including mobile phones, tablets, and desktop computers. We\'re optimized for both iOS and Android devices.'
        },
        {
          q: 'Do I need to create an account?',
          a: 'Yes, you need to create a free account to view and submit reports. This helps us maintain a trustworthy community while protecting your privacy.'
        }
      ]
    },
    {
      category: 'Using SafeHer',
      questions: [
        {
          q: 'How do I submit a safety report?',
          a: 'After logging in, click on the map where the incident occurred, then click "Report Incident". Fill in the details including the type of incident and description. You can choose to submit anonymously.'
        },
        {
          q: 'Can I edit or delete my reports?',
          a: 'Yes! You can edit or delete your own reports at any time from your profile page. Your reports remain under your control.'
        },
        {
          q: 'What do the different report levels mean?',
          a: 'Level 1: Uncomfortable situations or low-level concerns. Level 2: Harassment or following. Level 3: Serious or threatening situations. These levels help the community understand severity at a glance — they are not a substitute for contacting emergency services when needed.'
        },
        {
          q: 'How do I use the map filters?',
          a: 'Click the filter icon on the map to show/hide different types of reports. You can filter by severity level, time period, or report type to see exactly what you need.'
        }
      ]
    },
    {
      category: 'Safety & Privacy',
      questions: [
        {
          q: 'Are my reports anonymous?',
          a: 'Yes! All reports are automatically submitted anonymously to protect your safety and privacy. Your reports will always show as "Anonymous User" and no personal information is ever visible to other users.'
        },
        {
          q: 'Can people see my exact location?',
          a: 'No. Reports show the general area on the map, but your exact location or home address is never shared. You control what information you include.'
        },
        {
          q: 'How do you verify reports?',
          a: 'Our community moderators review reports for accuracy and appropriateness. Users can also upvote reports they\'ve experienced or witnessed, helping to validate information.'
        },
        {
          q: 'What happens to my data?',
          a: 'Your data is stored securely and never sold to third parties. You can delete your account and all associated data at any time. Read our privacy policy for full details.'
        }
      ]
    },
    {
      category: 'Community & Moderators',
      questions: [
        {
          q: 'What is the moderator program?',
          a: 'Moderators are community volunteers who help keep information on the map accurate. They review reports, remove spam or false posts, and can message reporters anonymously for more context. Moderators manage information on the platform only — they do not take any physical action or contact authorities.'
        },
        {
          q: 'How do I apply to be a moderator?',
          a: 'After being an active user for at least 30 days, you can apply through the "Request Moderator Access" button in your profile menu. Tell us why you want to help and what experience you have.'
        },
        {
          q: 'How do I report abuse or false information?',
          a: 'Click the flag icon on any report to report it. Moderators will review flagged content within 24 hours. You can also contact us directly through the help center.'
        },
        {
          q: 'What are the community guidelines?',
          a: 'Be respectful, honest, and supportive. Don\'t submit false reports, harass other users, or share personal information about others. Help us maintain a safe and trusting community.'
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="faq-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about SafeHer</p>
      </div>

      <div className="faq-content">
        {faqData.map((category, catIndex) => (
          <div key={catIndex} className="faq-category">
            <h2 className="category-title">{category.category}</h2>
            
            <div className="questions-list">
              {category.questions.map((item, qIndex) => {
                const index = `${catIndex}-${qIndex}`;
                const isOpen = openIndex === index;
                
                return (
                  <div 
                    key={qIndex} 
                    className={`faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggleQuestion(catIndex, qIndex)}
                    >
                      <span>{item.q}</span>
                      <span className="toggle-icon">{isOpen ? '−' : '+'}</span>
                    </button>
                    
                    {isOpen && (
                      <div className="faq-answer">
                        <p>{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="faq-footer">
        <h3>Still have questions?</h3>
        <p>We're here to help! Reach out through our help center.</p>
        <button 
          className="help-button"
          onClick={() => navigate('/help')}
        >
          Visit Help Center
        </button>
      </div>
    </div>
  );
};

export default FAQ;
